import { useMemo } from 'react';
import { CardTarget } from 'flows/core/types/cards';
import { getUserInfo_getUser_user } from 'types/generated/getUserInfo';
import { GetSubdomain } from 'utils/queries';
import { getSubdomain } from 'types/generated/getSubdomain';
import { ApolloError } from '@apollo/client';
import {
  AssurantEligibleOutput,
  Division,
  DivisionUtilitiesConfig,
  HomeServicesReservation,
  Item,
  useGetUtilitiesConfigQuery,
} from '@updater/consumer-graph';
import { useQueryWhenAuthed } from 'utils/use-query-when-authed';
import { useSWRWhenAuthed } from 'utils/use-swr-when-authed';
import { ItemDefinition } from 'types';
import { useGetUserInfoQuery } from './use-get-user-info-query';
import { useFeature } from '@growthbook/growthbook-react';
import { useAssurant } from 'flows/insurance/utils/assurant';
import { usePublicGridEligibility } from 'flows/public-grid/hooks/use-public-grid-eligibility';

type QueryData<T = Record<string, never>> = {
  loading: boolean;
  data?: T;
  error?: ApolloError | string;
};

export type ItemWithType = Item & { type: string };

export type DataForCards = {
  itemDefinitions: ItemDefinition[];
  items: ItemWithType[];
  user?: getUserInfo_getUser_user;
  divisionId?: Division;
  utilitiesConfig?: DivisionUtilitiesConfig;
  moverAppSubdomain?: string | null;
  isGreystarPilotEnabled?: boolean;
  /**
   * Whether Public Grid can transact at the resident's new address. Card makers
   * are pure functions and cannot ask for themselves, so it is resolved here and
   * passed to `isPublicGridActive` alongside the flag and the division config.
   */
  publicGridEligible?: boolean;
  insurance?: {
    AssurantEligibility: {
      eligible: boolean;
      variant?: AssurantEligibleOutput['variant'] | null;
      error?: unknown;
    };
  };
};

export type TargetConfigType = { [key: number | string]: string };

export interface VerticalCardDetails {
  items: ItemWithType[];
  target: string | TargetConfigType;
  tviData?: HomeServicesReservation;
}

export interface HomeServicesReservationVariables {
  id: string;
}

export const useDataForCards = (): QueryData<DataForCards> => {
  const { data: itemDefinitionsData, error: itemDefinitionsError } =
    useSWRWhenAuthed('/v2/item_definitions');
  const entityId = itemDefinitionsData?.find(
    (itemDefinition: ItemDefinition) => itemDefinition?.entity?.id
  )?.entity?.id;
  const { data: utilitiesConfigData, error: utilitiesConfigError } =
    useGetUtilitiesConfigQuery({
      variables: { entityId },
      skip: !entityId,
    });
  const {
    loading: cover360Loading,
    variant,
    eligible,
    error: cover360Error,
  } = useAssurant();
  const { data: itemsData, error: itemsError } = useSWRWhenAuthed('/v2/items');
  const { data: userData, error: userError } = useGetUserInfoQuery();
  const { data: subdomainData, error: subdomainError } =
    useQueryWhenAuthed<getSubdomain>(GetSubdomain);
  const { value: greystarAssurantPilotValue } = useFeature(
    'greystar_assurant_pilot_properties'
  );
  const isGreystarPilotEnabled = greystarAssurantPilotValue?.active === true;
  const {
    eligible: publicGridEligible,
    loading: publicGridEligibilityLoading,
  } = usePublicGridEligibility();

  return useMemo<QueryData<DataForCards>>(() => {
    const subdomainReady = !!subdomainData || !!subdomainError;
    if (
      itemsError ||
      itemDefinitionsError ||
      userError ||
      utilitiesConfigError
    ) {
      const rawError =
        itemsError || itemDefinitionsError || userError || utilitiesConfigError;
      return {
        loading: false,
        error:
          rawError instanceof ApolloError
            ? rawError
            : rawError instanceof Error
            ? rawError.message
            : String(rawError),
      };
    }
    if (
      utilitiesConfigData &&
      itemDefinitionsData &&
      itemsData &&
      userData &&
      subdomainReady &&
      !cover360Loading &&
      // Held until eligibility is known: resolving it later would show the
      // electric task and then swap it for the Public Grid card, or the reverse.
      !publicGridEligibilityLoading
    ) {
      return {
        loading: false,
        data: {
          itemDefinitions: itemDefinitionsData as ItemDefinition[],
          items: (
            itemsData as Array<ItemWithType & { move_id?: number }>
          )?.filter(({ move_id }) =>
            userData?.getUser?.user?.currentMove?.id
              ? move_id?.toString() === userData?.getUser?.user?.currentMove?.id
              : true
          ), // to get the current move items
          user: userData?.getUser?.user ?? undefined,
          moverAppSubdomain: subdomainData?.siteBranding?.subdomain,
          divisionId: entityId,
          utilitiesConfig: utilitiesConfigData?.utilitiesConfig ?? undefined,
          isGreystarPilotEnabled,
          publicGridEligible,
          insurance: {
            AssurantEligibility: {
              eligible: eligible ?? false,
              variant,
              error: cover360Error,
            },
          },
        },
      };
    }
    return { loading: true };
  }, [
    subdomainData,
    subdomainError,
    itemsError,
    itemDefinitionsError,
    userError,
    utilitiesConfigError,
    utilitiesConfigData,
    itemDefinitionsData,
    itemsData,
    userData,
    cover360Loading,
    entityId,
    isGreystarPilotEnabled,
    publicGridEligible,
    publicGridEligibilityLoading,
    eligible,
    variant,
    cover360Error,
  ]);
};

const convertRouteToUrlPath = (
  route: string,
  params?: Record<string, string | number | boolean>
) => {
  if (!params) return route;

  const appendedParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  return `${route}?${appendedParams}`;
};

export const buildURL = (action: CardTarget): string => {
  if (!action) return '';
  return action.type === 'external'
    ? action.url
    : convertRouteToUrlPath(action.route, action?.params);
};
